import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import cn from 'clsx';
import { useSnackbar } from 'notistack';

import { Button, Link } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import { ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM } from '~/consts/roles';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import { formatDateSlashesWithClockInSydney } from '~/services/TimeService';
import { AdminChatConversationDetails, getChatConversations } from '~/services/TripPlannerService';
import UsersService from '~/services/UsersService';

import type { UserSummaryType } from '~/types/responses';

import PermissionedComponent from '../Common/PermissionedComponent';

import TripPlannerChatSearchForm from './components/TripPlannerChatSearchForm';
import { TripPlannerDataGrid } from './components/TripPlannerDataGrid';
import TripSpoofDialog from './components/TripSpoofDialog';
import { TripPlannerChatSearchParams, useTripPlannerChatSearchParams } from './useTripPlannerChatSearchParams';

const DEFAULT_PAGE_SIZE = 20;

export default function TripPlannerChatConversationsPage() {
  const [conversations, setConversations] = useState<Array<AdminChatConversationDetails>>([]);
  const [userSummariesById, setUserSummariesById] = useState<Map<string, UserSummaryType>>(new Map());
  const [paginationModel, setPaginationModel] = useState({
    pageSize: DEFAULT_PAGE_SIZE,
    page: 0,
  });
  const [totalConversations, setTotalConversations] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [spoofUrl, setSpoofUrl] = useState<string>('');
  const [conversationName, setConversationName] = useState<string>('');
  const [spoofUrlCopied, setSpoofUrlCopied] = useState<boolean>(false);
  const [openSpoofingModal, setOpenSpoofingModal] = useState<boolean>(false);
  const latestFetchRequestId = useRef<number>(0);

  const { enqueueSnackbar } = useSnackbar();
  const { tenant: currentTenant } = useCurrentTenant();
  const { searchParams, updateSearchParams } = useTripPlannerChatSearchParams();

  useEffect(() => {
    setPaginationModel((previousPaginationModel) => ({
      ...previousPaginationModel,
      page: 0,
      pageSize: DEFAULT_PAGE_SIZE,
    }));
  }, [searchParams.chatId, searchParams.userId]);

  const flushSpoofingState = () => {
    setSpoofUrl('');
    setConversationName('');
    setSpoofUrlCopied(false);
    setOpenSpoofingModal(false);
  };

  const fetchConversations = useCallback(async () => {
    const requestId = latestFetchRequestId.current + 1;
    latestFetchRequestId.current = requestId;
    setLoading(true);

    try {
      const response = await getChatConversations({
        page: paginationModel.page + 1,
        pageSize: paginationModel.pageSize,
        chatId: searchParams.chatId,
        userId: searchParams.userId,
      });

      if (latestFetchRequestId.current !== requestId) {
        return;
      }

      if (response.status === 200) {
        const nextConversations = response.result.trips;
        const userIds = Array.from(
          new Set(nextConversations.map((conversation) => conversation.createdBy).filter(Boolean)),
        );
        let nextUserSummariesById = new Map<string, UserSummaryType>();

        if (userIds.length > 0) {
          try {
            nextUserSummariesById = await UsersService.getUsersSummaryByIds(userIds, currentTenant.brand);
          } catch {
            nextUserSummariesById = new Map();
          }
        }

        if (latestFetchRequestId.current !== requestId) {
          return;
        }

        setConversations(nextConversations);
        setUserSummariesById(nextUserSummariesById);
        setTotalConversations(response.result.numberOfTrips);
      }
    } catch {
      if (latestFetchRequestId.current !== requestId) {
        return;
      }

      setConversations([]);
      setUserSummariesById(new Map());
      setTotalConversations(0);
      enqueueSnackbar(
        'Invalid Conversation ID or User ID. Please ensure the IDs are entered correctly and try again.',
        {
          variant: 'error',
        },
      );
    } finally {
      if (latestFetchRequestId.current === requestId) {
        setLoading(false);
      }
    }
  }, [
    currentTenant.brand,
    enqueueSnackbar,
    paginationModel.page,
    paginationModel.pageSize,
    searchParams.chatId,
    searchParams.userId,
  ]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  const handleSearch = (nextSearchParams: TripPlannerChatSearchParams) => {
    setPaginationModel({
      pageSize: DEFAULT_PAGE_SIZE,
      page: 0,
    });
    updateSearchParams(nextSearchParams);
  };

  const handleSpoofConversation = useCallback(
    async (createdBy: string, chatRecordId: number, conversationTitle: string) => {
      flushSpoofingState();

      try {
        const generatedSpoofUrl = await UsersService.spoofUserTrip(
          createdBy,
          currentTenant,
          `/trip-planner/chat-trip-planner/${chatRecordId}`,
        );

        let copiedToClipboard = false;

        try {
          await navigator.clipboard.writeText(generatedSpoofUrl);
          copiedToClipboard = true;
        } catch {
          enqueueSnackbar(
            'Failed to copy spoof link to the clipboard. You can still copy it manually from the dialog.',
            {
              variant: 'error',
            },
          );
        }

        setSpoofUrl(generatedSpoofUrl);
        setConversationName(conversationTitle);
        setSpoofUrlCopied(copiedToClipboard);
        setOpenSpoofingModal(true);
      } catch {
        enqueueSnackbar('Failed to generate spoof link for the conversation. Please try again.', {
          variant: 'error',
        });
      }
    },
    [currentTenant, enqueueSnackbar],
  );

  const columns = useMemo<Array<GridColDef>>(
    () => [
      { field: 'name', headerName: 'Name', width: 200 },
      {
        field: 'createdAt',
        headerName: 'Created At (Sydney)',
        width: 200,
        valueFormatter: (value: string) => {
          return value ? formatDateSlashesWithClockInSydney(value) : '';
        },
      },
      { field: 'id', headerName: 'Conversation ID', width: 160 },
      {
        field: 'createdBy',
        headerName: 'User',
        width: 200,
        renderCell: (params) => {
          if (!userSummariesById.has(params.row.createdBy)) {
            return null;
          }

          return (
            <Link color="primary" rel="noreferrer" href={`/users/${params.row.createdBy}`} underline="hover">
              View User
            </Link>
          );
        },
        display: 'flex',
      },
      {
        field: 'spoofTrip',
        headerName: '',
        width: 150,
        renderCell: (params) => {
          if (!userSummariesById.has(params.row.createdBy)) {
            return null;
          }

          return (
            <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
              <Button
                variant="text"
                onClick={() => handleSpoofConversation(params.row.createdBy, params.row.id, params.row.name)}
              >
                Spoof Chat
              </Button>
            </PermissionedComponent>
          );
        },
        sortable: false,
        filterable: false,
        display: 'flex',
      },
    ],
    [handleSpoofConversation, userSummariesById],
  );

  return (
    <>
      <TripPlannerChatSearchForm initialValues={searchParams} onSearch={handleSearch} />

      <TripPlannerDataGrid
        rows={conversations}
        columns={columns}
        pagination
        paginationMode="server"
        rowCount={totalConversations}
        pageSizeOptions={[]}
        loading={loading}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        localeText={{
          noRowsLabel: 'No chat conversations found',
        }}
        getRowHeight={() => 'auto'}
        className={cn({ 'no-trip-items': conversations.length === 0 })}
      />

      <PermissionedComponent requiredRoles={[ROLE_ADMIN_USER, ROLE_EMPLOYEE_USER, ROLE_ICS_STORE_TEAM]}>
        <TripSpoofDialog
          openModal={openSpoofingModal}
          flushSpoofingState={flushSpoofingState}
          spoofUrl={spoofUrl}
          trip={conversationName}
          copiedToClipboard={spoofUrlCopied}
        />
      </PermissionedComponent>
    </>
  );
}
